import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink as RsNavLink,
  UncontrolledDropdown,
} from "reactstrap";
import useAuthStore from "../../stores/useAuthStore";
import classNames from "classnames";
import navAuth from "./_navAuth";
import navBarTeacher from "./_navBarTeacher";
import SidebarFooter from "../SidebarFooter/SidebarFooter";
import SidebarForm from "../SidebarForm/SidebarForm";
import SidebarHeader from "../SidebarHeader/SidebarHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OrganisationSearchBarContainer from "../Inputs/OrganisationSearchBarContainer";
import {
  GetSchoolDataBySchoolId,
  GetTeacherInformation,
} from "../../actions/teacherQueries";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StudentLicenseStatusIcon from "../Teacher/StudentLicenseStatusIcon";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { compareStrings } from "../../Utilities/sortHelper";
import { USER_TYPE_ICONS } from "../../Utilities/userTypeIcons";
import { toReadableUrl } from "../../Utilities/urlHelper";
import useChooseSidebarClassesStore from "../../stores/useChooseSidebarClassesStore";
import ChooseSidebarClassesModal from "../Modal/ChooseSidebarClassesModal";
import CreateClassInProgram from "../Modal/CreateClassInProgram";
import CreateStudentInClass from "../Modal/CreateStudentInClass";
import AddNewTeacherToClass from "../Modal/AddNewTeacherToClass";
import useAddNewTeacherToClass from "../Teacher/hooks/useAddNewTeacherToClass";

const AddModal = {
  CLASS: "Class",
  STUDENT: "Student",
  TEACHER: "Teacher",
};

export function useSidebarResize(min = 180, max = 800) {
  const frame = useRef(null);
  const latestWidth = useRef(null);
  const observer = useRef(null);

  const setSidebarWidth = (px) =>
    document.documentElement.style.setProperty("--sidebar-width", `${px}px`);

  const cancelFrame = () => {
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!document.documentElement.style.getPropertyValue("--sidebar-width")) {
      const saved = parseFloat(localStorage.getItem("sidebarWidth"));
      const width = !isNaN(saved) ? Math.max(min, Math.min(max, saved)) : 240;
      setSidebarWidth(width);
    }

    const setUp = () => {
      const resizer = document.querySelector(".sidebar-resizer");
      const sidebar = resizer?.closest(".sidebar");

      if (!resizer || !sidebar) return false;

      let startX = 0;
      let startWidth = 0;

      const onPointerDown = (e) => {
        startX = e.clientX;
        startWidth = sidebar.getBoundingClientRect().width;

        document.body.classList.add("sidebar-resizing");
        document.addEventListener("pointermove", onPointerMove);
        document.addEventListener("pointerup", onPointerUp);
      };

      const onPointerMove = (e) => {
        const dx = e.clientX - startX;
        const newWidth = Math.max(min, Math.min(max, startWidth + dx));
        latestWidth.current = newWidth;

        cancelFrame();
        frame.current = requestAnimationFrame(() => setSidebarWidth(newWidth));
      };

      const onPointerUp = () => {
        document.body.classList.remove("sidebar-resizing");
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);

        cancelFrame();
        if (latestWidth.current != null) {
          localStorage.setItem("sidebarWidth", latestWidth.current);
        }
      };

      resizer.addEventListener("pointerdown", onPointerDown);

      return () => {
        resizer.removeEventListener("pointerdown", onPointerDown);
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
        cancelFrame();
      };
    };

    let cleanup = setUp();

    if (!cleanup) {
      observer.current = new MutationObserver(() => {
        cleanup = setUp();
        if (cleanup) observer.current.disconnect();
      });
      observer.current.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (cleanup) cleanup();
      observer.current?.disconnect();
      cancelFrame();
    };
  }, [min, max]);
}

function Sidebar() {
  const intl = useIntl();
  useSidebarResize(180, 800);
  const location = useLocation();

  const role = useAuthStore((state) => state.roles);
  const isTeacher = role === "Teacher";
  const isStudent = role === "Student";

  const teacherInfo = GetTeacherInformation({
    enabled: isTeacher,
  });
  const openChooseSidebarClasses = useChooseSidebarClassesStore(
    (state) => state.open,
  );
  const [openAddModal, setOpenAddModal] = useState(null);
  const [programmeToAddTo, setProgrammeToAddTo] = useState(null);
  const [classToAddTo, setClassToAddTo] = useState(null);
  const schoolInfo = GetSchoolDataBySchoolId(
    openAddModal === AddModal.CLASS ? teacherInfo.data.customer.id : null,
  );
  const addNewTeacherToClass = useAddNewTeacherToClass();
  const closeAddModal = () => setOpenAddModal(null);

  useEffect(() => {
    if (openAddModal === AddModal.CLASS && schoolInfo.isError) {
      toast.warn(intl.formatMessage({ id: "CouldNotLoadData" }));
      closeAddModal();
    }
  }, [openAddModal, schoolInfo.isError, intl]);

  const getIndent = (type) => {
    switch (type) {
      case "program":
        return "0.7rem";
      case "class":
        return "2.5rem";
      case "hiddenClasses":
        return "calc(2.5rem + 20px + 1%)";
      case "teacher":
      case "student":
        return "4rem";
      default:
        return "1.0625rem";
    }
  };

  var handleClick = (e) => {
    e.preventDefault();
    const dropdown = e.currentTarget.closest(".nav-dropdown");
    if (dropdown) dropdown.classList.toggle("open");
  };

  var activeRoute = (routeName) => {
    return toReadableUrl(location.pathname).indexOf(routeName) > -1
      ? "nav-item nav-dropdown open"
      : "nav-item nav-dropdown";
  };

  var hideMobile = () => {
    if (document.body.classList.contains("sidebar-mobile-show")) {
      document.body.classList.toggle("sidebar-mobile-show");
    }
  };

  var showStudentName = (programName, stud, isAplProgram) => {
    const isInactive = !stud.activatedAccount;
    const className = isInactive ? "text-gray-300" : undefined;

    if (stud.requiredLicenseIsMissing) {
      return (
        <span className={className}>
          {stud.name}
          <StudentLicenseStatusIcon
            student={stud}
            programName={programName}
            isAplProgram={isAplProgram}
          />
        </span>
      );
    }

    if (isInactive) {
      return (
        <span
          className={className}
          title={intl.formatMessage({ id: "Student_NeedActivation" })}
        >
          {stud.name}
        </span>
      );
    }

    return stud.name;
  };

  var showProgramName = (prog) => {
    const programName = prog.name;
    if (prog.hasExpired) {
      return (
        <span
          title={intl.formatMessage(
            { id: "Program_NeedLicense" },
            { programName }
          )}
        >
          {programName}
          <WarningAmberIcon className="text-warning license-warning-icon" />
        </span>
      );
    }
    return programName;
  };

  var OrgStructure = (props) => {
    var orgStructure = [];
    if (teacherInfo?.data?.programmes !== undefined) {
      const hiddenClassIds = teacherInfo.data.hiddenClassIds ?? [];
      teacherInfo.data.programmes.forEach((prog) => {
        var classes = [];
        const shownClasses = prog.classes.filter(
          (cl) => !hiddenClassIds.includes(cl.id),
        );
        shownClasses.forEach((cl) => {
          var usersInClass = [];
          var sortingListUsersInClass = [];
          cl.students.forEach((stud) => {
            var student = {
              name: showStudentName(prog.name, stud, prog.isAplProgram),
              sortName: stud.name ?? "",
              url:
                "/teacher/programmeview/" +
                prog.id +
                "/" +
                prog.name +
                "/classview/" +
                cl.id +
                "/" +
                cl.name +
                "/studentview/" +
                stud.id +
                "/" +
                stud.name,
              icon: USER_TYPE_ICONS.Student,
              type: "student",
            };
            sortingListUsersInClass.push(student);
          });
          sortingListUsersInClass = sortingListUsersInClass.sort((a, b) =>
            compareStrings(a.sortName, b.sortName),
          );
          sortingListUsersInClass.forEach((user) => usersInClass.push(user));
          sortingListUsersInClass = [];
          cl.teachers.forEach((te) => {
            var teacher = {
              name: te.name,
              sortName: te.name ?? "",
              url:
                "/teacher/programmeview/" +
                prog.id +
                "/" +
                prog.name +
                "/classview/" +
                cl.id +
                "/" +
                cl.name +
                "/teacherview/" +
                te.id +
                "/" +
                te.name,
              icon: USER_TYPE_ICONS.Teacher,
              type: "teacher",
            };
            sortingListUsersInClass.push(teacher);
          });
          sortingListUsersInClass.sort((a, b) =>
            compareStrings(a.sortName, b.sortName),
          );
          sortingListUsersInClass.forEach((user) => usersInClass.push(user));
          const schoolClass = { id: cl.id, name: cl.name, programme: prog };
          var classo = {
            name: cl.name,
            sortName: cl.name ?? "",
            url:
              "/teacher/programmeview/" +
              prog.id +
              "/" +
              prog.name +
              "/classview/" +
              cl.id +
              "/" +
              cl.name,
            icon: "users",
            type: "class",
            children: usersInClass,
            addActions: [
              {
                name: intl.formatMessage(
                  { id: "AddNewStudentToNamedClass" },
                  { className: cl.name },
                ),
                icon: USER_TYPE_ICONS.Student,
                onClick: () => {
                  setClassToAddTo(schoolClass);
                  setOpenAddModal(AddModal.STUDENT);
                },
              },
              {
                name: intl.formatMessage(
                  { id: "AddNewTeacherToNamedClass" },
                  { className: cl.name },
                ),
                icon: USER_TYPE_ICONS.Teacher,
                onClick: () => {
                  setClassToAddTo(schoolClass);
                  setOpenAddModal(AddModal.TEACHER);
                },
              },
            ],
          };
          classes.push(classo);
        });
        classes = classes.sort((a, b) =>
          compareStrings(a.sortName, b.sortName),
        );
        const hiddenClassCount = prog.classes.length - shownClasses.length;
        if (hiddenClassCount > 0) {
          classes.push({
            name: intl.formatMessage(
              { id: "HiddenClassesCount" },
              { count: hiddenClassCount },
            ),
            icon: "eye-slash",
            type: "hiddenClasses",
            class: "fst-italic small",
            onClick: () => {
              hideMobile();
              openChooseSidebarClasses(prog.id);
            },
          });
        }
        var programme = {
          name: showProgramName(prog),
          sortName: prog.name ?? "",
          url: "/teacher/programmeview/" + prog.id + "/" + prog.name,
          icon: "briefcase",
          children: classes,
          type: "program",
          addActions: teacherInfo.data.fullAccess
            ? [
                {
                  name: intl.formatMessage({ id: "CreateNewClass" }),
                  onClick: () => {
                    setProgrammeToAddTo({
                      id: prog.id,
                      name: prog.name,
                      customer: teacherInfo.data.customer,
                    });
                    setOpenAddModal(AddModal.CLASS);
                  },
                },
              ]
            : [],
        };

        orgStructure.push(programme);
      });
    }
    orgStructure = orgStructure.sort((a, b) =>
      compareStrings(a.sortName, b.sortName),
    );
    return orgStructure;
  };

  // badge addon to NavItem
  const badge = (badge) => {
    if (badge) {
      const classes = classNames(badge.class);
      return (
        <Badge className={classes} color={badge.variant}>
          {badge.text}
        </Badge>
      );
    }
    return null;
  };

  // simple wrapper for nav-title item
  const wrapper = (item) => {
    return item.wrapper && item.wrapper.element
      ? React.createElement(
          item.wrapper.element,
          item.wrapper.attributes,
          item.name
        )
      : item.name;
  };

  // nav list section title
  const title = (title, key) => {
    const classes = classNames("nav-title", title.class);
    return (
      <li key={title + key} className={classes}>
        {wrapper(title)}{" "}
      </li>
    );
  };

  // nav list divider
  const divider = (divider, key) => {
    const classes = classNames("divider", divider.class);
    return <li key={key} className={classes} />;
  };

  // nav label with nav link
  const navLabel = (item, key) => {
    const classes = {
      item: classNames("hidden-cn", item.class),
      link: classNames("nav-label", item.class ? item.class : ""),
      icon: classNames(
        !item.icon ? "fa fa-circle" : item.icon,
        item.label.variant ? `text-${item.label.variant}` : "",
        item.label.class ? item.label.class : ""
      ),
    };
    return navLink(item, key, classes);
  };

  // nav item with nav link
  const navItem = (item, key) => {
    const classes = {
      item: classNames(item.class),
      link: classNames(
        "nav-link",
        item.variant ? `nav-link-${item.variant}` : ""
      ),
      icon: classNames(item.icon),
    };
    return navLink(item, key, classes);
  };

  // nav link
  const navLink = (item, key, classes) => {
    const url = item.url ? item.url : "";
    const indent = getIndent(item.type);

    return (
      <NavItem key={key} className={classes.item}>
        {isExternal(url) ? (
          <RsNavLink href={url} className={classes.link} active>
            <i>
              <FontAwesomeIcon icon={classes.icon} />
            </i>
            <span className="sidebar-label">{item.name}</span>
            {badge(item.badge)}
          </RsNavLink>
        ) : url !== null && url !== "" ? (
          <NavLink
            to={url}
            state={{ level: item.type, name: item.sortName ?? item.name }}
            style={{
              paddingLeft: indent,
              paddingTop: "0.25rem",
              paddingBottom: "0.25rem",
            }}
            className={classes.link}
            onClick={hideMobile}
          >
            <i>
              <FontAwesomeIcon icon={classes.icon} />
            </i>
            <span className="sidebar-label">{item.name}</span>
            {badge(item.badge)}
          </NavLink>
        ) : item.onClick ? (
          <a
            href="# "
            style={{
              paddingLeft: indent,
              paddingTop: "0.25rem",
              paddingBottom: "0.25rem",
              maxWidth: "240px",
            }}
            className={classes.link}
            onClick={(event) => {
              event.preventDefault();
              item.onClick();
            }}
          >
            <i>
              <FontAwesomeIcon icon={classes.icon} />
            </i>
            <span className="sidebar-label">{item.name}</span>
            {badge(item.badge)}
          </a>
        ) : (
          <span
            style={{
              paddingLeft: indent,
              paddingTop: "0.25rem",
              paddingBottom: "0.25rem",
              cursor: "default",
              maxWidth: "240px",
            }}
            className={classes.link}
          >
            <i>
              <FontAwesomeIcon icon={classes.icon} />
            </i>
            <span className="sidebar-label">{item.name}</span>
            {badge(item.badge)}
          </span>
        )}
      </NavItem>
    );
  };

  const runAddAction = (action) => {
    hideMobile();
    action.onClick();
  };

  const addButton = (item) => {
    if (!item.addActions?.length) {
      return "";
    }
    if (item.addActions.length === 1) {
      const action = item.addActions[0];
      return (
        <button
          type="button"
          className="btn btn-link text-reset px-2 py-0 custom-sidebar-add-button"
          title={action.name}
          onClick={() => runAddAction(action)}
        >
          <FontAwesomeIcon icon="plus" />
        </button>
      );
    }
    return (
      <UncontrolledDropdown className="d-flex">
        <DropdownToggle
          tag="button"
          type="button"
          className="btn btn-link text-reset px-2 py-0 custom-sidebar-add-button"
          title={intl.formatMessage({ id: "Add" })}
        >
          <FontAwesomeIcon icon="plus" />
        </DropdownToggle>
        <DropdownMenu end container="body" className="custom-sidebar-add-menu">
          {item.addActions.map((action) => (
            <DropdownItem
              key={action.name}
              onClick={() => runAddAction(action)}
            >
              <FontAwesomeIcon icon={action.icon} fixedWidth />
              {action.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  };

  // Dropdown and navlink
  const navItemAndDropdown = (item, key) => {
    const classes = {
      item: classNames(item.class),
      link: classNames(
        "nav-link",
        item.variant ? `nav-link-${item.variant}` : ""
      ),
      icon: classNames(item.icon),
    };
    const url = item.url ? item.url : "";
    const indent = getIndent(item.type);

    return (
      <li
        key={item.name + key}
        className={`nav-dropdown ${activeRoute(item.url)}`}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            paddingLeft: `${indent}`,
          }}
        >
          <a
            href="# "
            style={{
              flex: "0 0 auto",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "0",
              padding: "0",
              minWidth: "20px",
            }}
            className="nav-link custom-sidebar-nav-dropdown-toggle"
            onClick={(event) => handleClick(event)}
          >
            <FontAwesomeIcon icon="caret-right" />
          </a>
          <NavLink
            to={url}
            state={{ level: item.type, name: item.sortName ?? item.name }}
            className={classes.link}
            style={{
              flex: 1,
              paddingTop: "0.2rem",
              paddingBottom: "0.2rem",
              paddingLeft: item.type === "class" ? "1%" : "0",
            }}
            onClick={hideMobile}
          >
            <i>
              <FontAwesomeIcon icon={classes.icon} />
            </i>
            <span className="sidebar-label">{item.name}</span>
            {badge(item.badge)}
          </NavLink>
          {addButton(item)}
        </div>

        <ul className="nav-dropdown-items" style={{ margin: 0, padding: 0 }}>
          {navList(item.children)}
        </ul>
      </li>
    );
  };

  // nav type
  const navType = (item, idx) =>
    item.title
      ? title(item, idx)
      : item.divider
      ? divider(item, idx)
      : item.label
      ? navLabel(item, idx)
      : item.children && (item.type === "class" || item.type === "program")
      ? navItemAndDropdown(item, idx)
      : navItem(item, idx);

  // nav list
  const navList = (items) => {
    return items.map((item, index) =>
      !item.isProgramsAndClasses ? (
        navType(item, index)
      ) : (
        <ul
          key={item.name + index}
          style={{ listStyleType: "none" }}
          className="ps-0"
        >
          {" "}
          {item.programsAndClasses.map((orgItem, orgIndex) =>
            navType(orgItem, orgIndex)
          )}
        </ul>
      )
    );
  };

  const isExternal = (url) => {
    const link = url ? url.substring(0, 4) : "";
    return link === "http";
  };

  let navBar = <Nav />;

  if (role === null) {
    return <div></div>;
  }

  // Navlists
  //Student
  if (isStudent) {
    navBar = <Nav>{navList(navAuth(intl).items)}</Nav>;
  }
  //Teacher
  else if (isTeacher) {
    if (teacherInfo.isLoading) {
      return null;
    }

    //Get query
    var teacherNavBar = navBarTeacher(OrgStructure(teacherInfo.data), intl);
    navBar = <Nav>{navList(teacherNavBar)}</Nav>;
  }
  //Superuser/Admin
  else {
    navBar = <Nav></Nav>;
  }

  // sidebar-nav root
  return (
    <div className="sidebar">
      <SidebarHeader />
      <SidebarForm />

      {isTeacher ? (
        <OrganisationSearchBarContainer
          teacherInfo={teacherInfo}
          searchCriterias={["students", "classes", "programs"]}
          placeHolder={intl.formatMessage({ id: "SearchStudentEmailClassOrProgram" })}
          theme="dark"
        />
      ) : (
        ""
      )}

      <nav className="sidebar-nav">{navBar}</nav>
      {isTeacher ? <ChooseSidebarClassesModal /> : ""}
      {isTeacher ? (
        <>
          <CreateClassInProgram
            isOpen={openAddModal === AddModal.CLASS && schoolInfo.isSuccess}
            toggle={closeAddModal}
            teachers={schoolInfo.data?.teachers}
            program={programmeToAddTo}
          />
          <CreateStudentInClass
            addNewStudentToClassModal={openAddModal === AddModal.STUDENT}
            schoolClass={classToAddTo}
            toggle={closeAddModal}
            purchaserTeacher={teacherInfo.data?.purchaser}
          />
          <AddNewTeacherToClass
            isOpen={openAddModal === AddModal.TEACHER}
            toggle={closeAddModal}
            schoolClass={classToAddTo}
            onSubmit={(values) => {
              closeAddModal();
              addNewTeacherToClass(values, classToAddTo.id);
            }}
            fullAccessTeacher={teacherInfo.data?.fullAccess}
            purchaserTeacher={teacherInfo.data?.purchaser}
          />
        </>
      ) : (
        ""
      )}
      <SidebarFooter />

      <div className="sidebar-resizer" />
    </div>
  );
}
export default Sidebar;
